import { round2 } from "../format-utils"
import { getHoursPerDayOrDefault } from "routes/rate-cards/capacity-constants.mjs"

/**
 * Capacity assessment for a fixed-annual contract row (FIQ-1735).
 *
 * Compares the days the scenario requires (raw task hours ÷ hours per day) against the
 * contracted days bought by the annual amount. Returns null when the line has no contracted
 * capacity, so callers can leave behaviour completely unchanged in that case.
 *
 * @param {{ hours?: number, amount?: number, contractedDaysPerYear?: number }} contract
 * @param {{ hoursPerDay?: number }} [capacity] - Rate card capacity config from the cost result
 * @returns {null | {
 *   contractedDays: number,
 *   requiredDays: number,
 *   overCapacity: boolean,
 *   headroomDays: number,
 *   shortfallDays: number,
 *   extrapolatedCost: number,
 *   additionalCost: number,
 * }}
 */
export function getFixedContractCapacity(contract, capacity) {
    const contractedDays = contract?.contractedDaysPerYear
    if (!(typeof contractedDays === "number" && contractedDays > 0)) return null

    const hoursPerDay = getCapacityHoursPerDay(capacity)
    // Capacity is a core rate-card costing invariant used by every cost-result consumer.
    // Raw task hours are deliberate: minimum billing periods must not inflate workload.
    const requiredDays = round2((contract.hours || 0) / hoursPerDay)
    const amount = contract.amount || 0
    const overCapacity = requiredDays > contractedDays
    const extrapolatedCost = overCapacity ? round2((amount / contractedDays) * requiredDays) : amount

    return {
        amount,
        contractedDays,
        requiredDays,
        overCapacity,
        headroomDays: overCapacity ? 0 : round2(contractedDays - requiredDays),
        shortfallDays: overCapacity ? round2(requiredDays - contractedDays) : 0,
        extrapolatedCost,
        additionalCost: overCapacity ? round2(extrapolatedCost - amount) : 0,
    }
}

export function getCapacityHoursPerDay(capacity) {
    return getHoursPerDayOrDefault(capacity?.hoursPerDay)
}
